import { useCallback, useEffect, useMemo, useState } from 'react';

import { createNexusApi } from '../../../../domain/nexus/api';
import type { GoogleNexusDashboard } from '../../../../domain/nexus/model';
import { firebaseNexusGateway } from '../../../../platform/firebase/nexusGateway';
import { logGlobalError } from '../../../../utils/globalErrorLogger';
import { diagnosticMessage, isPollableNexusUpload } from './helpers';

const nexusApi = createNexusApi(firebaseNexusGateway);

export interface GoogleNexusViewModel {
  busy: boolean;
  loading: boolean;
  error: string | null;
  dashboard: GoogleNexusDashboard | null;
  hasActiveUploads: boolean;
  initialize: () => Promise<void>;
  refresh: () => Promise<void>;
  clearError: () => void;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return diagnosticMessage(error);
}

export function useGoogleNexusViewModel(): GoogleNexusViewModel {
  const [busy, setBusy] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<GoogleNexusDashboard | null>(null);

  const loadDashboard = useCallback(async (showLoading: boolean) => {
    if (showLoading) setLoading(true);
    try {
      const next = await nexusApi.fetchDashboard();
      setDashboard(next);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  const initialize = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await loadDashboard(false);
    } catch (nextError) {
      setError(errorMessage(nextError));
      logGlobalError(nextError, { context: 'GoogleNexusTool.initialize' });
    } finally {
      setBusy(false);
    }
  }, [loadDashboard]);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      await loadDashboard(true);
    } catch (nextError) {
      setError(errorMessage(nextError));
      logGlobalError(nextError, { context: 'GoogleNexusTool.refresh' });
    }
  }, [loadDashboard]);

  const clearError = useCallback(() => setError(null), []);

  const hasActiveUploads = useMemo(
    () => dashboard?.uploads.some(isPollableNexusUpload) ?? false,
    [dashboard],
  );

  // Background refresh keeps the table current while validations run; the
  // detail page does the per-upload Nexus polling.
  useEffect(() => {
    if (!hasActiveUploads) return undefined;
    const interval = window.setInterval(() => {
      void nexusApi
        .refreshActiveUploads()
        .then(() => loadDashboard(false))
        .catch(() => {
          // Best-effort; the next tick retries.
        });
    }, 30_000);
    return () => window.clearInterval(interval);
  }, [hasActiveUploads, loadDashboard]);

  return {
    busy,
    loading,
    error,
    dashboard,
    hasActiveUploads,
    initialize,
    refresh,
    clearError,
  };
}
